import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { alterarMaterial, criarMaterial, enviarArquivo, excluirMaterial, listarMateriais } from '../api/materiais';
import type { Material } from '../api/materiais';
import { listarCampos } from '../../conteudo/api/conteudo';

interface MateriaisPainelProps {
    csrfToken: string;
}

/** Mesmo teto do servidor: recusar aqui evita subir 30 MB para ouvir um "não". */
export const TETO_DO_ARQUIVO = 30 * 1024 * 1024;
/** Até saber quantos enigmas o Scan Pixel tem hoje (o admin inclui e exclui na aba Variáveis). */
const ENIGMAS_DO_PADRAO = 5;

const paraEnigma = (valor: string) => (valor === '' ? null : Number(valor));

function tamanhoLegivel(bytes: number): string {
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function mensagemDoErro(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp) {
        if (typeof erro.corpo?.mensagem === 'string') return erro.corpo.mensagem;
        if (erro.status === 413) return 'O PDF passa de 30 MB.';
        if (erro.status === 401 || erro.status === 403) return 'Sessão expirada ou sem permissão. Entre de novo.';
    }
    return 'Não foi possível concluir. Tente novamente.';
}

export function MateriaisPainel({ csrfToken }: MateriaisPainelProps) {
    const [materiais, setMateriais] = useState<Material[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [novo, setNovo] = useState({ titulo: '', enigma: '' });

    const [totalDeEnigmas, setTotalDeEnigmas] = useState(ENIGMAS_DO_PADRAO);

    const carregar = useCallback(async () => {
        try {
            const lista = await listarMateriais();
            setMateriais(lista);
            // O seletor acompanha o catálogo; se a leitura falhar, fica o que já estava na tela.
            const enigmas = await listarCampos('scanpixel')
                .then((dados) => dados.estrutura?.itens.filter((item) => item.entidade === 'enigma').length ?? 0)
                .catch(() => 0);
            // Um material preso a um enigma que saiu continua aparecendo no seletor dele.
            const maiorEmUso = Math.max(0, ...lista.map((material) => material.enigma_ordem ?? 0));
            if (enigmas > 0) setTotalDeEnigmas(Math.max(enigmas, maiorEmUso));
        } catch (falha) {
            setMateriais([]);
            setErro(mensagemDoErro(falha));
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    const ENIGMAS = Array.from({ length: totalDeEnigmas }, (_, i) => i + 1);

    async function executar(acao: () => Promise<string | void>) {
        setErro(null);
        setAviso(null);
        setOcupado(true);
        try {
            setAviso((await acao()) ?? null);
            await carregar();
        } catch (falha) {
            setErro(mensagemDoErro(falha));
        } finally {
            setOcupado(false);
        }
    }

    function aoCriar(evento: FormEvent) {
        evento.preventDefault();
        void executar(async () => {
            await criarMaterial({ titulo: novo.titulo.trim(), enigma_ordem: paraEnigma(novo.enigma) }, csrfToken);
            setNovo({ titulo: '', enigma: '' });
        });
    }

    function aoEscolherArquivo(material: Material, evento: ChangeEvent<HTMLInputElement>) {
        const arquivo = evento.target.files?.[0];
        // Limpa o campo: escolher o mesmo arquivo de novo precisa disparar outro envio.
        evento.target.value = '';
        if (!arquivo) return;
        if (arquivo.size > TETO_DO_ARQUIVO) {
            setAviso(null);
            setErro(`O PDF passa de 30 MB (${tamanhoLegivel(arquivo.size)}).`);
            return;
        }
        void executar(async () => {
            await enviarArquivo(material.id, arquivo, csrfToken);
            return `PDF de «${material.titulo}» enviado.`;
        });
    }

    function excluir(material: Material) {
        if (!window.confirm(`Excluir o material «${material.titulo}» e o PDF dele?`)) return;
        void executar(async () => {
            await excluirMaterial(material.id, csrfToken);
        });
    }

    if (materiais === null) return <p className="estado-variaveis">Carregando materiais…</p>;

    return (
        <div className="cadastro">
            {erro && <p className="mensagem-login aviso-cadastro" role="alert">{erro}</p>}
            {aviso && <p className="mensagem-salvo" role="status">{aviso}</p>}

            <section className="bloco-cadastro" aria-labelledby="materiais-novo">
                <h3 id="materiais-novo">Novo material</h3>
                <form className="form-cadastro" aria-label="Novo material" onSubmit={aoCriar}>
                    <label>
                        Título
                        <input
                            value={novo.titulo} maxLength={120} required placeholder="Guia Comercial Argo X"
                            onChange={(e) => setNovo({ ...novo, titulo: e.target.value })}
                        />
                    </label>
                    <label>
                        Liberação
                        <select value={novo.enigma} onChange={(e) => setNovo({ ...novo, enigma: e.target.value })}>
                            <option value="">Sempre liberado</option>
                            {ENIGMAS.map((n) => <option key={n} value={n}>Ao resolver o enigma {n}</option>)}
                        </select>
                    </label>
                    <button className="botao-entrar" type="submit" disabled={ocupado}>Criar material</button>
                </form>
            </section>

            <section className="bloco-cadastro" aria-labelledby="materiais-lista">
                <h3 id="materiais-lista">Biblioteca</h3>
                {materiais.length === 0 ? (
                    <p className="estado-variaveis">Nenhum material cadastrado. Crie o primeiro e envie o PDF.</p>
                ) : (
                    <table className="tabela-jogadores">
                        <thead>
                            <tr>
                                <th scope="col">Ordem</th>
                                <th scope="col">Título</th>
                                <th scope="col">Liberação</th>
                                <th scope="col">PDF</th>
                                <th scope="col">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {materiais.map((material) => (
                                <tr key={material.id}>
                                    <td className="numero">{material.ordem}</td>
                                    <td>{material.titulo}</td>
                                    <td>
                                        <select
                                            className="selecao-tabela" disabled={ocupado}
                                            aria-label={`Liberação de ${material.titulo}`}
                                            value={material.enigma_ordem ?? ''}
                                            onChange={(e) => void executar(async () => {
                                                await alterarMaterial(material.id, { enigma_ordem: paraEnigma(e.target.value) }, csrfToken);
                                            })}
                                        >
                                            <option value="">Sempre liberado</option>
                                            {ENIGMAS.map((n) => <option key={n} value={n}>Enigma {n}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        {material.arquivo_nome
                                            ? <><code>{material.arquivo_nome}</code> · {tamanhoLegivel(material.arquivo_bytes ?? 0)}</>
                                            : <span className="sem-tablet">Sem PDF</span>}
                                    </td>
                                    <td className="acoes-cadastro">
                                        <label className="botao-sair botao-arquivo">
                                            {material.arquivo_nome ? 'Trocar PDF' : 'Enviar PDF'}
                                            <input
                                                type="file" accept="application/pdf,.pdf" disabled={ocupado}
                                                aria-label={`${material.arquivo_nome ? 'Trocar' : 'Enviar'} PDF de ${material.titulo}`}
                                                onChange={(e) => aoEscolherArquivo(material, e)}
                                            />
                                        </label>
                                        <button
                                            className="botao-sair" type="button" disabled={ocupado}
                                            aria-label={`Excluir ${material.titulo}`}
                                            onClick={() => excluir(material)}
                                        >
                                            Excluir
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
